import type { DepthData, RgbaData } from './types';

/** Decodes an image file/blob into RGBA pixels, downscaling to `maxSide`. */
export async function decodeImage(blob: Blob, maxSide = 4096): Promise<RgbaData> {
  const bmp = await createImageBitmap(blob);
  const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * scale));
  const h = Math.max(1, Math.round(bmp.height * scale));
  const canvas = new OffscreenCanvas(w, h);
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(bmp, 0, 0, w, h);
  bmp.close();
  const data = ctx.getImageData(0, 0, w, h).data;
  return { width: w, height: h, data };
}

export function depthToRgba(d: DepthData): RgbaData {
  const out = new Uint8ClampedArray(d.width * d.height * 4);
  for (let i = 0; i < d.data.length; i++) {
    const v = Math.round(Math.min(1, Math.max(0, d.data[i])) * 255);
    out[i * 4] = out[i * 4 + 1] = out[i * 4 + 2] = v;
    out[i * 4 + 3] = 255;
  }
  return { width: d.width, height: d.height, data: out };
}

export function drawToCanvas(canvas: HTMLCanvasElement, img: RgbaData) {
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d')!;
  const data =
    img.data instanceof Uint8ClampedArray
      ? img.data
      : new Uint8ClampedArray(img.data.buffer, img.data.byteOffset, img.data.byteLength);
  ctx.putImageData(new ImageData(data as Uint8ClampedArray<ArrayBuffer>, img.width, img.height), 0, 0);
}

export function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Fits `w x h` into `maxW x maxH` keeping aspect ratio, with even dimensions. */
export function fitSize(w: number, h: number, maxW: number, maxH = maxW, upscale = false): [number, number] {
  const s = Math.min(upscale ? Infinity : 1, maxW / w, maxH / h);
  return [Math.max(2, Math.round((w * s) / 2) * 2), Math.max(2, Math.round((h * s) / 2) * 2)];
}

export function cloneRgba(img: RgbaData): RgbaData {
  return { width: img.width, height: img.height, data: new Uint8Array(img.data) };
}

export function cloneDepth(d: DepthData): DepthData {
  return { width: d.width, height: d.height, data: new Float32Array(d.data) };
}
